import { isValid, parseISO } from 'date-fns';
import { format, toZonedTime } from 'date-fns-tz';

export const DateFormat = {
  ISO_DATE: 'yyyy-MM-dd',
  MONTH_SHORT_DAY_SHORT: 'MMM d',
  MONTH_SHORT_DAY_SHORT_YEAR: 'MMM d, yyyy',
  WEEKDAY_MONTH_SHORT_DAY_SHORT_YEAR: 'eee MMM d, yyyy',
  MONTH_LONG: 'MMMM',
  MONTH_SHORT: 'MMM',
  MONTH_NUMBER: 'MM',
  DAY_OF_MONTH: 'd',
  DAY_NUMBER: 'dd',
  FULL_YEAR: 'yyyy',
  MONTH_LONG_DAY_SHORT_YEAR_TIME: 'MMMM d, yyyy h:mm a',
  ISO_SHORT_TIME: 'h:mm a',
  MILITARY_TIME: 'HH:mm',
} as const;

const getDateFormatString = (locale: string) => {
  const formatObj = new Intl.DateTimeFormat(locale).formatToParts(new Date());

  return formatObj
    .map((obj) => {
      switch (obj.type) {
        case 'day':
          return DateFormat.DAY_NUMBER;
        case 'month':
          return DateFormat.MONTH_NUMBER;
        case 'year':
          return DateFormat.FULL_YEAR;
        default:
          return obj.value;
      }
    })
    .join('');
};

const getLocaleHelpers = () => {
  const { timeZone, locale } = Intl.DateTimeFormat().resolvedOptions();
  const localeFormatStr = getDateFormatString(locale);

  return { timeZone, localeFormatStr };
};

/**
 * @name formatLocalDate
 * @summary Format ISO Date String to browser locale format
 *
 * @param dateString the date in ISO String format
 * @param formatStr(optional) the format to use.  Will default to browser locale if not provided
 *
 * @example
 * // Given September 8, 2025 08:38 am (in Seattle, WA USA (US Pacific))
 * const result = formatLocalDate('2025-09-08T15:38:30.000Z')
 * //=> "09/08/2025"
 *
 * // Given September 8, 2025 08:38 am (in Seattle, WA USA (US Pacific)), formatted for German
 * const result = formatLocalDate('2025-09-08T15:38:30.000Z')
 * //=> "08.09.2025"
 *
 */
export const formatLocalDate = (dateString: string | Date, formatStr?: string) => {
  if (!dateString) {
    return '-';
  }

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  const { timeZone, localeFormatStr } = getLocaleHelpers();
  const zonedDate = toZonedTime(date, timeZone);

  return format(zonedDate, formatStr ?? localeFormatStr, { timeZone });
};

export const formatDateAsLocal = (dateString: string, formatStr?: string) => {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  const { localeFormatStr } = getLocaleHelpers();
  const adjustedDate = new Date(date.valueOf() + date.getTimezoneOffset() * 60 * 1000);

  return format(adjustedDate, formatStr ?? localeFormatStr);
};

export const formatLocalDateWithTime = (dateString: string, timeStr?: string) => {
  const { localeFormatStr } = getLocaleHelpers();
  return formatLocalDate(dateString, `${localeFormatStr} ${timeStr ?? DateFormat.ISO_SHORT_TIME}`);
};

export const toISOTimestamp = (dateString: string | null | undefined): string | null => {
  if (!dateString) return null;
  return parseISO(dateString).toISOString();
};

/**
 * Creates a Date at UTC midnight for the given year, month, and day.
 * @param year - The full year (e.g., 2025)
 * @param month - The month (0-11, where 0 = January, 11 = December)
 * @param day - The day of the month (1-31)
 */
export const toUTCMidnight = (year: number, month: number, day: number): Date => {
  return new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
};

/**
 * Normalizes a date to UTC midnight, preserving the calendar date in UTC timezone.
 */
export const dateToUTCMidnight = (date: Date): Date => {
  return toUTCMidnight(date.getFullYear(), date.getMonth(), date.getDate());
};

/**
 * Converts a UTC midnight date string to a Date at local midnight with the same calendar date.
 * Used for displaying date-only values in Calendar components without timezone shifts.
 */
export const toLocalMidnight = (dateString: string): Date | undefined => {
  const parsedDate = fromDateString(dateString);
  if (!parsedDate) return undefined;
  return new Date(parsedDate.getUTCFullYear(), parsedDate.getUTCMonth(), parsedDate.getUTCDate());
};

/**
 * Parses a date string (YYYY-MM-DD or ISO format) as UTC midnight, avoiding timezone shifts.
 * Returns null for invalid dates (e.g., 2025-02-31).
 */
export const fromDateString = (dateStr: string | null | undefined): Date | null => {
  if (!dateStr) return null;

  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;

  const [, yearStr = '', monthStr = '', dayStr = ''] = match;
  const year = Number.parseInt(yearStr, 10);
  const month = Number.parseInt(monthStr, 10);
  const day = Number.parseInt(dayStr, 10);

  const date = toUTCMidnight(year, month - 1, day);

  if (
    !isValid(date) ||
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }

  return date;
};
